import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { useToast } from "../lib/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBoxesStacked, faArrowLeft, faCartPlus } from "@fortawesome/free-solid-svg-icons";
import { Tilt } from "../lib/interactions";
import type { Product } from "../lib/types";

const fmt = (n: number) => n.toLocaleString("ko-KR");

export default function ProductDetail() {
  const { sku = "" } = useParams();
  const [product, setProduct] = useState<Product | null>(null);
  const [related, setRelated] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [qty, setQty] = useState(1);
  const { add, items: cartItems, update: cartUpdate } = useCart();
  const { push } = useToast();
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    setRelated([]);
    api.get<{ product: Product }>(`/products/${encodeURIComponent(sku)}`)
      .then((r) => {
        setProduct(r.product);
        setQty(1);
        // 같은 카테고리 다른 상품 6개
        if (r.product.category_slug) {
          api.get<{ items: Product[] }>(`/products?category=${r.product.category_slug}`)
            .then((rs) => setRelated(rs.items.filter((p) => p.id !== r.product.id).slice(0, 6)))
            .catch(() => {});
        }
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [sku]);

  if (loading) {
    return (
      <main id="main-content" className="container" style={{ padding: "var(--s-8) var(--s-5)" }}>
        <div className="skel" style={{ height: 320, marginBottom: 16 }} />
        <div className="skel" style={{ height: 24, width: "60%", marginBottom: 8 }} />
        <div className="skel" style={{ height: 16, width: "40%" }} />
      </main>
    );
  }

  if (err || !product) {
    return (
      <main id="main-content" className="container" style={{ padding: "var(--s-10) var(--s-5)", textAlign: "center" }}>
        <h1 className="h-title">상품을 찾을 수 없습니다</h1>
        <p className="muted" style={{ marginTop: 8 }}>{err || "SKU 확인 필요"}</p>
        <Link to="/catalog" className="btn btn-primary" style={{ marginTop: 16 }}>카탈로그로 돌아가기</Link>
      </main>
    );
  }

  const inCart = cartItems.find((it) => it.product.id === product.id);

  const addToCart = () => {
    if (inCart) { cartUpdate(product.id, qty); } else { add(product, qty); }
    push(`장바구니에 담았습니다 (${qty}${product.unit})`, "success");
  };

  const buyNow = () => {
    if (inCart) { cartUpdate(product.id, qty); } else { add(product, qty); }
    nav("/cart");
  };

  return (
    <main id="main-content" className="container" style={{ padding: "var(--s-8) var(--s-5)" }}>
      <Link to="/catalog" className="btn btn-ghost btn-sm" style={{ marginBottom: 16 }}>
        <FontAwesomeIcon icon={faArrowLeft} style={{ width: 12, height: 12 }} /> 카탈로그
      </Link>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1.1fr) 1fr", gap: "var(--s-10)" }} className="pd-grid">
        <div>
          <div aria-hidden="true" style={{ aspectRatio: "1/1", background: "var(--bg-muted)", borderRadius: "var(--r-md)", display: "grid", placeItems: "center", color: "var(--border-strong)", overflow: "hidden", position: "relative" }}>
            {product.image_url ? (
              <img src={product.image_url} alt={product.name}
                style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
            ) : (
              <FontAwesomeIcon icon={faBoxesStacked} style={{ width: 96, height: 96 }} />
            )}
          </div>
        </div>

        <div>
          <div className="muted mono" style={{ fontSize: 15, letterSpacing: "0.05em", marginBottom: 6 }}>SKU {product.sku}</div>
          {product.category_name && (
            <div className="eyebrow" style={{ marginBottom: 10 }}>{product.category_name}</div>
          )}
          <h1 className="h-title" style={{ fontSize: 28, marginBottom: 8 }}>{product.name}</h1>
          <p className="muted" style={{ marginBottom: 20, fontSize: 15 }}>{product.brand} · {product.spec}</p>

          <div style={{ padding: "var(--s-5)", background: "var(--bg-subtle)", borderRadius: "var(--r-md)", marginBottom: "var(--s-5)" }}>
            <div className="row" style={{ alignItems: "baseline", gap: 4 }}>
              <span style={{ fontSize: 32, fontWeight: 800, fontFamily: "var(--font-display)", color: "var(--primary)" }}>{fmt(product.price)}</span>
              <span className="muted" style={{ fontSize: 15 }}>원 / {product.unit}</span>
            </div>
            <div className="muted" style={{ fontSize: 15, marginTop: 4 }}>회원 등급 단가 적용됨 · VAT 별도</div>
          </div>

          <table style={{ marginBottom: "var(--s-5)" }}>
            <tbody>
              <Row k="재고" v={<span className={`badge ${product.stock > 50 ? "badge-success" : product.stock > 0 ? "badge-warning" : "badge-danger"}`}>{product.stock > 0 ? `${fmt(product.stock)} ${product.unit}` : "품절"}</span>} />
              <Row k="단위" v={product.unit} />
              <Row k="브랜드" v={product.brand ?? "—"} />
              <Row k="규격" v={product.spec ?? "—"} />
            </tbody>
          </table>

          {product.description && (
            <div style={{ padding: "var(--s-4)", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--r-sm)", fontSize: 15, lineHeight: 1.7, whiteSpace: "pre-wrap", marginBottom: "var(--s-5)" }}>
              {product.description}
            </div>
          )}

          <div className="row" style={{ gap: 12, alignItems: "center", marginBottom: "var(--s-4)" }}>
            <label style={{ fontSize: 15, fontWeight: 600 }}>수량</label>
            <input type="number" value={qty} min={1} max={product.stock}
              onChange={(e) => setQty(Math.max(1, Math.min(product.stock, Number(e.target.value) || 1)))}
              style={{ width: 120 }} />
            <span className="muted" style={{ fontSize: 15 }}>
              합계: <strong style={{ color: "var(--primary)" }}>{fmt(product.price * qty)}원</strong>
            </span>
          </div>

          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-outline" onClick={addToCart} disabled={product.stock === 0}>
              <FontAwesomeIcon icon={faCartPlus} style={{ width: 14, height: 14 }} /> 장바구니 담기
            </button>
            <button className="btn btn-cta" onClick={buyNow} disabled={product.stock === 0}>
              바로 주문하기
            </button>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section style={{ marginTop: "var(--s-12)" }}>
          <p className="eyebrow" style={{ marginBottom: 6 }}>같은 카테고리</p>
          <h2 className="h-title" style={{ fontSize: 22, marginBottom: 16 }}>{product.category_name}의 다른 상품</h2>
          <div className="rel-grid">
            {related.map((p) => (
              <Link key={p.id} to={`/catalog/${encodeURIComponent(p.sku)}`} style={{ display: "block" }}>
                <Tilt max={5} className="card" style={{ padding: "var(--s-3)", display: "flex", flexDirection: "column", gap: 6 }}>
                  <div aria-hidden="true" style={{ aspectRatio: "1/1", background: "var(--bg-muted)", borderRadius: "var(--r-sm)", display: "grid", placeItems: "center", color: "var(--border-strong)" }}>
                    <FontAwesomeIcon icon={faBoxesStacked} style={{ width: 28, height: 28 }} />
                  </div>
                  <div className="mono muted" style={{ fontSize: 15 }}>{p.sku}</div>
                  <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.name}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--primary)", marginTop: "auto" }}>{fmt(p.price)}<span className="muted" style={{ fontSize: 15, fontWeight: 400 }}>원/{p.unit}</span></div>
                </Tilt>
              </Link>
            ))}
          </div>
        </section>
      )}

      <style>{`
        @media (max-width: 720px) {
          .pd-grid { grid-template-columns: 1fr !important; gap: var(--s-5) !important; }
        }
        .rel-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: var(--s-3);
        }
      `}</style>
    </main>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <tr>
      <th style={{ width: 130 }}>{k}</th>
      <td>{v}</td>
    </tr>
  );
}
